import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EmptyState, Page } from '@/design-system/Layout';
import { ROUTES } from '@/router/routes';

import { PlanAction } from './components/PlanAction';
import { VesselDetail } from './components/VesselDetail';
import { toDetailVessel } from './subscription.detail';
import { isLifetimePlan } from './subscription.display';
import { isPaidByAnother } from './subscription.takeover';
import { useVesselBill } from './useVesselBill';
import { useVesselSeats } from './useVesselSeats';
import { useVesselSubscription } from './useVesselSubscription';

/**
 * `/:vesselId` — one vessel: its bill, its seats, and the control that changes its plan.
 *
 * The id in the URL is the vessel's, not its subscription's, because the vessel is what
 * survives: a free vessel has no subscription row to name, and cancelling and resubscribing
 * mints a new one, which would break the link. It is also the service id, so it addresses
 * both halves of this view on its own.
 *
 * The detail model is derived on every render, so a plan change is reflected as soon as the
 * refetch lands. The seat meters still come from the mocked model — see `toDetailVessel`
 * for what the API does and does not report.
 */
export function VesselDetailPage() {
  const { vesselId } = useParams();
  const navigate = useNavigate();

  const { item, isLoading, isUnavailable } = useVesselSubscription(vesselId);

  // Prices and people are only fetched here — the list states plans, not amounts, and
  // nothing in it needs to know who holds a seat.
  const bill = useVesselBill(item);
  const seats = useVesselSeats(item);

  // Mistyped, deleted, or simply not theirs: there is no view to show and the list is where
  // they came from. Only this case redirects — a failure that might clear on a reload says
  // so and stays put, rather than bouncing the user off a URL that could be perfectly good.
  useEffect(() => {
    if (isUnavailable) navigate(ROUTES.HOME, { replace: true });
  }, [isUnavailable, navigate]);

  if (!item) {
    return (
      <Page>
        <EmptyState>{isLoading ? 'Loading vessel…' : 'Could not load this vessel.'}</EmptyState>
      </Page>
    );
  }

  return (
    <Page>
      <VesselDetail
        vessel={toDetailVessel(item)}
        // Buying a seat is the same `PATCH` as `PlanAction`'s Change plan, so the same gate —
        // stricter on payer: no plan on somebody else's card, whoever is looking.
        subscription={
          item.canEdit && !isPaidByAnother(item) && !isLifetimePlan(item.subscription)
            ? item.subscription
            : null
        }
        bill={bill}
        seats={seats}
        planAction={<PlanAction {...item} />}
        paidByAnother={isPaidByAnother(item)}
        onBack={() => navigate(ROUTES.HOME)}
      />
    </Page>
  );
}
